import React, { Component } from 'react'
import {
    Form,
    Card,
    Button,
    Upload,
    Select,
    Divider,
    Image,
    Space,
    Popconfirm,
    message
} from 'antd'
import styled from 'styled-components'
import {
    FontColorsOutlined,
    UploadOutlined,
    DeleteOutlined
} from '@ant-design/icons'
import { CirclePicker } from 'react-color'
import {
    getAppConfig,
    setAppConfig,
    uploadImage
} from '../../services/apiServices'
import RowLayout from '../../commons/RowLayout/RowLayout'
import LoadingModal from '../../commons/Modal/Loading'
import ColorSelector from '../../commons/ColorSelector/ColorSelector'
import SaveButton from '../../commons/Button/SaveButton'

const { Option } = Select

const Container = styled(Card)`
    .image-wrapper {
        display: inline-block;
        padding: 20px;
        margin-top: 15px;
        border: 1px dashed #ccc;
        > img {
            max-width: 400px;
        }
    }
`

const COLORS = [
    '#e74c3c',
    '#e91e63',
    '#9c27b0',
    '#673ab7',
    '#3f51b5',
    '#2196f3',
    '#03a9f4',
    '#00bcd4',
    '#009688',
    '#4caf50',
    '#8bc34a',
    '#cddc39',
    '#ffeb3b',
    '#ffcc00',
    '#ff9800',
    '#ff5722',
    '#795548',
    '#2c3e50'
]

const FONTS = [
    { key: '', name: 'NO_SELECT' },
    { key: 'Anuphan', name: 'Anuphan' },
    { key: 'Athiti', name: 'Athiti' },
    { key: 'Bai+Jamjuree', name: 'Bai Jamjuree' },
    { key: 'Chakra+Petch', name: 'Chakra Petch' },
    { key: 'Fahkwang', name: 'Fahkwang' },
    { key: 'IBM+Plex+Sans+Thai', name: 'IBM Plex Sans Thai' },
    { key: 'IBM+Plex+Sans+Thai+Looped', name: 'IBM Plex Sans Thai Looped' },
    { key: 'K2D', name: 'K2D' },
    { key: 'Kanit', name: 'Kanit' },
    { key: 'Kodchasan', name: 'Kodchasan' },
    { key: 'Krub', name: 'Krub' },
    { key: 'Maitree', name: 'Maitree' },
    { key: 'Mali', name: 'Mali' },
    { key: 'Mitr', name: 'Mitr' },
    { key: 'Niramit', name: 'Niramit' },
    { key: 'Noto+Sans+Thai', name: 'Noto Sans Thai' },
    { key: 'Noto+Sans+Thai+Looped', name: 'Noto Sans Thai Looped' },
    { key: 'Noto+Serif+Thai', name: 'Noto Serif Thai' },
    { key: 'Pridi', name: 'Pridi' },
    { key: 'Prompt', name: 'Prompt' },
    { key: 'Sarabun', name: 'Sarabun' },
    { key: 'Taviraj', name: 'Taviraj' },
    { key: 'Trirong', name: 'Trirong' }
]

class Tone extends Component {
    state = {
        isLoading: false,
        logo: '',
        favicon: {
            xs: '',
            md: '',
            lg: ''
        },
        primaryColor: '',
        fontFamily: ''
    }

    componentDidMount() {
        this.getAppConfig()
    }

    onFinish() {
        this.setAppConfig()
    }

    async getAppConfig() {
        this.setState({ isLoading: true })
        try {
            let {
                logo,
                favicon = {},
                primary_color,
                font_family
            } = await getAppConfig()
            this.setState({
                isLoading: false,
                logo,
                favicon,
                primaryColor: primary_color,
                fontFamily: font_family
            })
        } catch (error) {
            this.setState({ isLoading: false })
            message.error(this.props.T('SOMETHING_WRONG'))
        }
    }

    async setAppConfig() {
        this.setState({ isLoading: true })
        try {
            await setAppConfig({
                body: {
                    config: {
                        logo: this.state.logo,
                        favicon: this.state.favicon,
                        primary_color: this.state.primaryColor,
                        font_family: this.state.fontFamily
                    }
                }
            })
            this.setState({
                isLoading: false
            })
            message.success(this.props.T('SAVED'))
        } catch (error) {
            this.setState({ isLoading: false })
            message.error(this.props.T('SOMETHING_WRONG'))
        }
    }

    async uploadImage(file, sizes = [], onSuccess) {
        this.setState({ isLoading: true })
        try {
            let { image, images = [] } = await uploadImage({
                body: {
                    file,
                    sizes: JSON.stringify(sizes)
                }
            })
            this.setState({ isLoading: false })
            onSuccess && onSuccess(image, images)
        } catch (error) {
            this.setState({ isLoading: false })
            message.error(this.props.T('SOMETHING_WRONG'))
        }
    }

    onChangeLogo(e) {
        this.uploadImage(e.file.originFileObj, [], (image, images) => {
            this.setState({ logo: image })
        })
    }

    onRemoveLogo() {
        this.setState({ logo: '' })
    }

    onChangeFavicon(e) {
        let sizes = [
            { width: 16, height: 16, crop: false },
            { width: 32, height: 32, crop: false },
            { width: 96, height: 96, crop: false }
        ]
        this.uploadImage(e.file.originFileObj, sizes, (image, images) => {
            if (images.length == sizes.length) {
                this.setState({
                    favicon: {
                        xs: images[0],
                        md: images[1],
                        lg: images[2]
                    }
                })
            }
        })
    }

    onRemoveFavicon() {
        this.setState({
            favicon: {
                xs: '',
                md: '',
                lg: ''
            }
        })
    }

    onChangePrimaryColor = color => {
        this.setState({ primaryColor: color })
    }

    onChangeFontFamily(fontFamily = '') {
        this.setState({ fontFamily })
    }

    render() {
        let { T } = this.props

        return (
            <Form onFinish={this.onFinish.bind(this)}>
                <Container
                    title={
                        <span className="text-xl">
                            <FontColorsOutlined /> {T('LOGO_TONE')}
                        </span>
                    }>
                    <RowLayout title={T('LOGO')} marginBottom={24}>
                        <Space>
                            <Upload
                                accept="image/gif, image/jpeg, image/png, image/webp"
                                fileList={[]}
                                customRequest={() => {}}
                                onChange={e => this.onChangeLogo(e)}>
                                <Button type="primary">
                                    <UploadOutlined /> {T('UPLOAD')}
                                </Button>
                            </Upload>
                            {this.state.logo && (
                                <Popconfirm
                                    placement="top"
                                    title={T('CONFIRM_REMOVE_MENU_MSG')}
                                    onConfirm={() => this.onRemoveLogo()}
                                    okText={T('YES')}
                                    cancelText={T('NO')}>
                                    <Button
                                        type="primary"
                                        danger
                                        icon={<DeleteOutlined />}></Button>
                                </Popconfirm>
                            )}
                        </Space>
                        {this.state.logo && (
                            <div>
                                <div className="image-wrapper">
                                    <Image src={this.state.logo} alt="logo" />
                                </div>
                            </div>
                        )}
                    </RowLayout>

                    <RowLayout
                        title="Favicon"
                        tooltip={
                            <Image
                                src={`${window.config.themeUrl}/manager/static/images/favicon-location.png`}
                                alt="favicon"
                            />
                        }
                        description=".png (92x92 px)">
                        <Space>
                            <Upload
                                accept="image/png"
                                fileList={[]}
                                customRequest={() => {}}
                                onChange={e => this.onChangeFavicon(e)}>
                                <Button type="primary">
                                    <UploadOutlined /> {T('UPLOAD')}
                                </Button>
                            </Upload>
                            {this.state.favicon.lg && (
                                <Popconfirm
                                    placement="top"
                                    title={T('CONFIRM_REMOVE_MENU_MSG')}
                                    onConfirm={() => this.onRemoveFavicon()}
                                    okText={T('YES')}
                                    cancelText={T('NO')}>
                                    <Button
                                        type="primary"
                                        danger
                                        icon={<DeleteOutlined />}></Button>
                                </Popconfirm>
                            )}
                        </Space>

                        {this.state.favicon.lg && (
                            <div>
                                <div className="image-wrapper">
                                    <Image
                                        src={this.state.favicon.lg}
                                        alt="favicon"
                                    />
                                </div>
                            </div>
                        )}
                    </RowLayout>

                    <Divider />

                    <RowLayout title={T('PRIMARY_COLOR')}>
                        <div style={{ marginBottom: 15 }}>
                            <CirclePicker
                                width={400}
                                color={this.state.primaryColor}
                                colors={COLORS}
                                onChange={color =>
                                    this.onChangePrimaryColor(color.hex)
                                }
                            />
                        </div>
                        <div>
                            <div style={{ marginBottom: 10 }}>{T('OR')}</div>
                            <ColorSelector
                                disableRemoveIcon
                                color={this.state.primaryColor}
                                onChange={this.onChangePrimaryColor.bind(this)}
                            />
                        </div>
                    </RowLayout>

                    <Divider />

                    <RowLayout title={T('FONT')}>
                        <Form.Item>
                            <Select
                                value={this.state.fontFamily}
                                style={{ width: 200 }}
                                onChange={this.onChangeFontFamily.bind(this)}>
                                {FONTS.map(item => (
                                    <Option key={item.key} value={item.key}>
                                        {T(item.name)}
                                    </Option>
                                ))}
                            </Select>
                            <a
                                href="https://fonts.google.com/?subset=thai&preview.text=ยินดีต้อนรับสู่%20Indytheme&preview.text_type=custom&stylecount=6"
                                target="_blank">
                                <Button type="link">
                                    {T('PREVIEW_ALL_FONTS')}
                                </Button>
                            </a>
                        </Form.Item>
                    </RowLayout>

                    <LoadingModal isOpen={this.state.isLoading} />
                </Container>
                <SaveButton T={T} />
            </Form>
        )
    }
}

export default Tone
